import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../assets/ChangePassword.css';

const ChangePassword = () => {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');

    // Check password strength for new password
    if (name === 'newPassword') {
      checkPasswordStrength(value);
    }
  };

  const checkPasswordStrength = (password) => {
    if (password.length === 0) {
      setPasswordStrength('');
      return;
    }

    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) setPasswordStrength('weak');
    else if (strength === 3) setPasswordStrength('fair');
    else if (strength === 4) setPasswordStrength('good');
    else setPasswordStrength('strong');
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 'weak': return 'Weak - Add uppercase, numbers, and symbols';
      case 'fair': return 'Fair - Consider adding symbols';
      case 'good': return 'Good - Strong password';
      case 'strong': return 'Strong - Excellent security';
      default: return '';
    }
  };

  const getPasswordStrengthClass = () => {
    return `strength-${passwordStrength}`;
  };

  const validateForm = () => {
    if (!formData.currentPassword) {
      setError('Current password is required');
      return false;
    }
    if (!formData.newPassword) {
      setError('New password is required');
      return false;
    }
    if (formData.newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return false;
    }
    if (passwordStrength === 'weak') {
      setError('Please choose a stronger password');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/change-password', {
        current_password: formData.currentPassword,
        new_password: formData.newPassword
      });

      if (response.data.success) {
        setShowSuccessModal(true);
        // Update the user context to reflect the password change
        if (user) {
          const updatedUser = { ...user, password_changed: true };
          setUser(updatedUser);
        }
      }
    } catch (error) {
      console.error('Password change error:', error);
      setError(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessConfirm = () => {
    setShowSuccessModal(false);
    // Redirect to login page
    navigate('/login');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <div className="change-password-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-7 col-md-9">
              <div className="emr-card">
                {/* EMR Header */}
                <div className="emr-header">
                  <div className="emr-header-content">
                    <div className="emr-logo">
                      <i className="fas fa-shield-alt"></i>
                    </div>
                    <h1 className="emr-title">Security Update Required</h1>
                    <p className="emr-subtitle">
                      Welcome to Health.IQ Research Platform - Please update your password for enhanced security
                    </p>
                  </div>
                </div>

                {/* EMR Body */}
                <div className="emr-body">
                  {/* Security Badge */}
                  

                  {/* Error Alert */}
                  {error && (
                    <div className="emr-alert emr-alert-danger">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>{error}</span>
                    </div>
                  )}

                  {/* Password Change Form */}
                  <form onSubmit={handleSubmit}>
                    {/* Current Password */}
                    <div className="emr-form-group">
                      <label htmlFor="currentPassword" className="emr-label">
                        <i className="fas fa-key"></i>
                        Current Password
                      </label>
                      <div style={{ position: 'relative' }}>
                        <i className="fas fa-lock emr-input-icon"></i>
                        <input
                          type="password"
                          className="emr-input"
                          id="currentPassword"
                          name="currentPassword"
                          value={formData.currentPassword}
                          onChange={handleInputChange}
                          placeholder="Enter your current password"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>

                    {/* New Password */}
                    <div className="emr-form-group">
                      <label htmlFor="newPassword" className="emr-label">
                        <i className="fas fa-keyboard"></i>
                        New Password
                      </label>
                      <div style={{ position: 'relative' }}>
                        <i className="fas fa-shield-alt emr-input-icon"></i>
                        <input
                          type="password"
                          className="emr-input"
                          id="newPassword"
                          name="newPassword"
                          value={formData.newPassword}
                          onChange={handleInputChange}
                          placeholder="Create a strong new password"
                          required
                          disabled={loading}
                          minLength="8"
                        />
                      </div>
                      {formData.newPassword && (
                        <div className={`password-strength ${getPasswordStrengthClass()}`}>
                          <small>{getPasswordStrengthText()}</small>
                        </div>
                      )}
                    </div>

                    {/* Confirm New Password */}
                    <div className="emr-form-group">
                      <label htmlFor="confirmPassword" className="emr-label">
                        <i className="fas fa-check-double"></i>
                        Confirm New Password
                      </label>
                      <div style={{ position: 'relative' }}>
                        <i className="fas fa-check emr-input-icon"></i>
                        <input
                          type="password"
                          className="emr-input"
                          id="confirmPassword"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          placeholder="Re-enter your new password"
                          required
                          disabled={loading}
                          minLength="8"
                        />
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="emr-actions">
                      <button
                        type="submit"
                        className="emr-btn-primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="loading-spinner"></span>
                            Updating Security Settings...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save"></i>
                            Update Password & Continue
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        className="emr-btn-secondary"
                        onClick={handleLogout}
                        disabled={loading}
                      >
                        <i className="fas fa-sign-out-alt"></i>
                        Logout Instead
                      </button>
                    </div>
                  </form>

                  {/* Security Tips */}
                  <div className="mt-4 pt-4 border-top">
                    <h6 className="text-muted mb-3">
                      <i className="fas fa-lightbulb text-warning me-2"></i>
                      Password Security Tips
                    </h6>
                    <div className="row text-small">
                      <div className="col-md-6">
                        <ul className="list-unstyled">
                          <li className="mb-2">
                            <i className="fas fa-check text-success me-2"></i>
                            Use at least 8 characters
                          </li>
                          <li className="mb-2">
                            <i className="fas fa-check text-success me-2"></i>
                            Include uppercase & lowercase
                          </li>
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <ul className="list-unstyled">
                          <li className="mb-2">
                            <i className="fas fa-check text-success me-2"></i>
                            Add numbers & symbols
                          </li>
                          <li className="mb-2">
                            <i className="fas fa-check text-success me-2"></i>
                            Avoid common words
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal">
            <div className="success-icon">
              <i className="fas fa-check"></i>
            </div>
            <h2>Password Updated Successfully!</h2>
            <p>
              Your password has been changed and your account security has been enhanced.
              You will now be redirected to the login page to sign in with your new credentials.
            </p>
            <button
              className="emr-btn-primary"
              onClick={handleSuccessConfirm}
            >
              <i className="fas fa-sign-in-alt me-2"></i>
              Proceed to Login
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChangePassword;